import { useState, useMemo, useEffect } from 'react';

// Video asset imports — handled by Vite's asset pipeline
import loadingLoop from '../../assets/videos/loading-loop.mp4';
import surpriseCat from '../../assets/videos/surprise-cat.mp4';
import surpriseKnock from '../../assets/videos/surprise-knock.mp4';
import surprisePixelFace from '../../assets/videos/surprise-pixel-face.mp4';
import surpriseMiniTv from '../../assets/videos/surprise-mini-tv.mp4';
import surprisePuzzle from '../../assets/videos/surprise-puzzle.mp4';

const SURPRISE_CLIPS = [
  surpriseCat,
  surpriseKnock,
  surprisePixelFace,
  surpriseMiniTv,
  surprisePuzzle,
];

/**
 * Full-window loading overlay. The video fills the entire GUI window
 * (object-fit: cover), cropping rather than letterboxing so there are
 * no black bars at the edges regardless of aspect mismatch. The status
 * text + spinner sit at the bottom over a dark gradient scrim so the
 * copy stays legible no matter what's playing in that part of the
 * frame.
 *
 * 80% chance of the standard loading loop, 20% chance of a random
 * surprise clip; the surprise clip plays through once then falls back
 * to the loop so a long-running operation doesn't end on a frozen
 * end-of-clip frame.
 *
 * When `introClip` is supplied the caller pins a specific one-shot
 * clip in place of the random 80/20 pick — it plays through ONCE then
 * falls back to the loading loop via the same fallback path as a
 * surprise clip. The deploy overlay uses this to play
 * `crucible_bifrost.mp4` the instant a build is sent to Bifrost (#663)
 * — the visual signal is "your game is shipping to the player platform
 * now." The rolling deploy-status text still renders over the clip,
 * and the overlay stays up for the whole (long-running) deploy, so
 * this is purely the intro frame of the deploy overlay rather than a
 * separate dismissible player.
 */
export function LoadingOverlay({
  message,
  isVisible,
  introClip,
}: {
  message: string;
  isVisible: boolean;
  introClip?: string;
}): React.JSX.Element | null {
  // Pick video once when the overlay becomes visible — stable across
  // re-renders while isVisible stays true. useMemo with isVisible +
  // introClip deps recalculates only when visibility or the pinned
  // clip changes.
  const { videoSrc, shouldLoop } = useMemo(() => {
    if (!isVisible) return { videoSrc: loadingLoop, shouldLoop: true };
    // A caller-supplied intro clip wins over the random pick — play it
    // once, then fall back to the loop via the surpriseEnded path.
    if (introClip) return { videoSrc: introClip, shouldLoop: false };
    const isSurprise = Math.random() < 0.2;
    if (isSurprise) {
      const clip = SURPRISE_CLIPS[Math.floor(Math.random() * SURPRISE_CLIPS.length)];
      return { videoSrc: clip, shouldLoop: false };
    }
    return { videoSrc: loadingLoop, shouldLoop: true };
  }, [isVisible, introClip]);

  // Track whether the one-shot clip (a surprise clip, or a pinned
  // `introClip`) has finished so we can fall back to the loop.
  const [surpriseEnded, setSurpriseEnded] = useState(false);

  // Reset surpriseEnded when the overlay becomes visible again.
  useEffect(() => {
    if (isVisible) setSurpriseEnded(false);
  }, [isVisible]);

  if (!isVisible) return null;

  const activeSrc = !shouldLoop && surpriseEnded ? loadingLoop : videoSrc;
  const activeLoop = shouldLoop || surpriseEnded;

  return (
    <div
      // Issue #965 — `app-region-no-drag` defence-in-depth. The
      // overlay is fully opaque (`bg-black` + the looping video)
      // so a drag-region bleed-through is harmless USER-perception-
      // wise (there's nothing visible to click on the cancel-deploy
      // path here), but the symmetry matters: every `fixed inset-0
      // z-50+` overlay in the renderer must carry this carve-out so
      // a future addition of a click affordance (cancel button,
      // diagnostics link, etc.) can't accidentally reintroduce the
      // bleed-through. The structural drag-region test pins the
      // invariant — see PreviewOverlay.exit-affordances.test.tsx.
      className="app-region-no-drag fixed inset-0 z-50 overflow-hidden bg-black"
      role="status"
      aria-live="polite"
      aria-label={message}
    >
      {/* Video fills the full window. object-cover crops rather than
          letterboxes so widescreen clips on a tall window (or vice
          versa) don't show black bars. The black background colour
          on the wrapper is just a fallback for the moments before
          the first frame paints. */}
      <video
        key={activeSrc}
        src={activeSrc}
        autoPlay
        muted
        loop={activeLoop}
        playsInline
        onEnded={() => {
          if (!shouldLoop) setSurpriseEnded(true);
        }}
        className="absolute inset-0 h-full w-full object-cover"
      />

      {/* Bottom gradient scrim for status-text legibility — keeps the
          copy readable regardless of what's in the lower third of the
          frame at any given moment. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/85 via-black/50 to-transparent"
        aria-hidden="true"
      />

      {/* Spinner + status text — overlaid on the bottom-centre over
          the scrim. Padding keeps it clear of the macOS traffic-light
          gutter on a real frame and away from the very edge on
          maximised windows. */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-3 pb-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/30 border-t-crucible-accent" />
        <p className="px-6 text-center text-base text-white/90 [text-shadow:_0_1px_3px_rgb(0_0_0_/_60%)]">
          {message}
        </p>
      </div>
    </div>
  );
}
